import styles from '../focus-import.module.css';
import { Typography } from '@SBGSports/referee-react';
import { useTranslations } from 'i18n';
import { AthleteParticipationByTagType } from './athlete-participation-by-tag-type';
import { IconAlert } from '@SBGSports/referee-react/icons';
import { useEffect, useMemo, useRef } from 'react';
import { ButtonTypeAction, PageAttributeProps } from 'shared/modals/multi-page-modal/types';
import { useMultiPageWizard } from 'shared/modals/multi-page-modal/use-multi-page-wizard';
import { getUniqueFocusTags } from 'focus/focus-utils';
import { useFocusImport } from 'focus/use-focus-import';
import mixpanel from 'mixpanel-browser';
import { EVENTS } from 'analytics/analytics-events';
import { useFocusImportSelections } from '../use-focus-import-selections';
import { useFocusAnnotationCategoryMapper } from '../use-focus-annotation-category-mapper';
import { useFocusStoredOptions } from 'focus/use-focus-stored-options';
import { LEARN_MORE_LINK } from 'focus/constants';
import { useFocusAthleteAnnotationCategoryMapper } from '../use-focus-athlete-annotation-category-mapper';
import { useFocusAthleteAnnotationMapper } from '../use-focus-athlete-annotation-mapper';
import { useHandleCreateAnnotations } from '../use-handle-create-annotations';

export const MapAthletesScreen: React.FC<PageAttributeProps> = (props) => {
    const { setButtonDetails } = props;
    const { __ } = useTranslations();
    const { setHeader } = useMultiPageWizard();
    const { focusFile, activityId } = useFocusImport();
    const isError = false; // TODO: update to change when "Import" fail/success
    const { selectionsRef, setAthleteAssignment } = useFocusImportSelections();
    const mapper = useFocusAnnotationCategoryMapper();
    const { focusStoredOptions, setFocusStoredOptions } = useFocusStoredOptions();
    const categoryMapper = useFocusAthleteAnnotationCategoryMapper();
    const annotationMapper = useFocusAthleteAnnotationMapper(activityId);
    const handleImport = useHandleCreateAnnotations(focusFile, categoryMapper, annotationMapper);

    /**
     * Set header and custom button details here on first render to display on parent component
     */
    const executedRef = useRef(false);
    useEffect(() => {
        if (executedRef.current) {
            return;
        }
        mixpanel.track(EVENTS.focus.mapAthletes, {});
        mixpanel.time_event(EVENTS.focus.mapAthletesEnded);
        const shouldDisable = selectionsRef.current.athleteAssignment.size === 0;
        setButtonDetails?.([
            {
                type: ButtonTypeAction.advance,
                text: __('smt_importer.import'),
                isDisabled: shouldDisable,
                action: async () => {
                    await handleImport(selectionsRef.current, () =>
                        setFocusStoredOptions({ ...focusStoredOptions, isBaking: true }),
                    );
                },
            },
        ]);
        setHeader(
            <Typography variant="heading-2">
                {__('smt_importer.map_athletes_title', { productName: 'Focus' })}
            </Typography>,
        );
        executedRef.current = true;
        return () => {
            mixpanel.track(EVENTS.focus.mapAthletesEnded, {});
        };
    }, [
        __,
        setHeader,
        setButtonDetails,
        mapper,
        focusFile,
        selectionsRef,
        setFocusStoredOptions,
        focusStoredOptions,
        handleImport,
    ]);

    const uniqueFocusTags: string[] = useMemo(() => {
        return getUniqueFocusTags(focusFile?.tags ?? []);
    }, [focusFile?.tags]);

    return (
        <div className={styles['start-page-body']}>
            {isError && (
                <div className={styles['error-message']}>
                    <IconAlert color="var(--r-alert)" />
                    <Typography variant="body-2">{__('reporting.try_again')}</Typography>
                    <Typography variant="body-1">{__('smt_importer.try_again_description')}</Typography>
                </div>
            )}
            <div>
                <Typography variant="body-1">
                    {__('smt_importer.map_athletes_description', { productName: 'Focus' }).concat(' ')}
                    <a target="window" href={LEARN_MORE_LINK} className={styles['learn_more_link']}>
                        {__('misc.learn_more')}
                    </a>
                </Typography>
            </div>
            <AthleteParticipationByTagType
                uniqueFocusTags={uniqueFocusTags}
                onValueChanged={(athleteAssignment) => {
                    setAthleteAssignment(athleteAssignment);
                    executedRef.current = false;
                }}
            />
        </div>
    );
};
